import { useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { SecurityPage } from '../account/SecurityPage';
import { AuditPage } from '../admin/AuditPage';
import { BackupsPage } from '../admin/BackupsPage';
import { ReleasePage } from '../admin/ReleasePage';
import { UsersPage } from '../admin/UsersPage';
import { ComponentsPage } from '../components/ComponentsPage';
import { ImportsPage } from '../imports/ImportWizard';
import { PrintStationPage } from '../labels/PrintStationPage';
import { FreeformPrintPage } from '../labels/FreeformPrintPage';
import { TemplatesPage } from '../labels/TemplatesPage';
import { PrinterProfilesPage } from '../labels/PrinterProfilesPage';
import { PrinterProfileQrPage } from '../labels/PrinterProfileQrPage';
import { LocationsPage } from '../locations/LocationsPage';
import { ProjectsPage } from '../projects/ProjectsPage';
import { PurchasesPage } from '../purchases/PurchasesPage';
import { ScanPage } from '../scan/ScanPage';
import { offerIncomingPrinterProfile, takeIncomingScan } from '../scan/incoming';
import { useOpenScanned } from '../scan/useOpenScanned';
import { SettingsPage } from '../settings/SettingsPage';
import { SpecsPage } from '../specs/SpecsPage';
import { StockPage } from '../stock/StockPage';
import { StocktakesPage } from '../stocktakes/StocktakesPage';
import { AppShell } from './AppShell';
import type { Focus, HistoryFocus } from './focus';

/**
 * The record named by the address, if there is one.
 *
 * The history key rides along as `seq`: navigating to the address already open
 * — scanning the same shelf twice — pushes a new entry with a new key, and that
 * is what tells the page to act on it again.
 */
function useFocus(): Focus | null {
  const { id } = useParams();
  const { key } = useLocation();
  return useMemo(() => (id ? { id, seq: key } : null), [id, key]);
}

function ComponentsRoute() {
  return <ComponentsPage focus={useFocus()} />;
}

function LocationsRoute() {
  return <LocationsPage focus={useFocus()} />;
}

function PurchasesRoute() {
  return <PurchasesPage focus={useFocus()} />;
}

function ProjectsRoute() {
  return <ProjectsPage focus={useFocus()} />;
}

/** A specification is opened by one of its versions — that is what a kit box's
 *  label names — so the version travels as a query rather than as the path. */
function SpecsRoute() {
  const [params] = useSearchParams();
  const { key } = useLocation();
  const version = params.get('version');
  const focus = useMemo(() => (version ? { id: version, seq: key } : null), [version, key]);
  return <SpecsPage focusVersion={focus} />;
}

/** The audit log is indexed by table and row together: "component 7" and
 *  "location 7" are different records, so both halves are in the address. */
function AuditRoute() {
  const [params] = useSearchParams();
  const { key } = useLocation();
  const id = params.get('entity_id');
  const entityType = params.get('entity_type') ?? '';
  const focus = useMemo<HistoryFocus | null>(
    () => (id ? { id, entityType, seq: key } : null),
    [id, entityType, key],
  );
  return <AuditPage focus={focus} />;
}

function ScanRoute() {
  const openScanned = useOpenScanned();
  const navigate = useNavigate();
  const { state, key } = useLocation();
  // Consumed on arrival at this screen and never re-read from the address: a
  // code from a printed QR is a one-off event, not a property of the address.
  //
  // Re-read on every arrival, though, and not only on the first render. In the
  // Android application a label opens the application itself: the second label
  // scanned finds this screen already open, and a code read once at mount would
  // be a code that only ever works the first time.
  const [fromLabel, setFromLabel] = useState(takeIncomingScan);
  useEffect(() => {
    const offered = takeIncomingScan();
    if (offered) setFromLabel(offered);
  }, [key]);
  // A scanner read something the server does not know. This is the only screen
  // that can do anything about that, so the code comes here with it.
  const unresolved = (state as { unresolved?: string } | null)?.unresolved ?? null;
  return <ScanPage
    initial={unresolved ?? fromLabel}
    onOpen={openScanned}
    onPrinterProfile={(id) => { offerIncomingPrinterProfile(id); navigate('/print'); }}
  />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<Navigate to="/components" replace />} />
        <Route path="components/:id?" element={<ComponentsRoute />} />
        <Route path="locations/:id?" element={<LocationsRoute />} />
        <Route path="stock" element={<StockPage />} />
        <Route path="stocktakes/:id?" element={<StocktakesPage />} />
        <Route path="purchases/:id?" element={<PurchasesRoute />} />
        <Route path="specs/:id?" element={<SpecsRoute />} />
        <Route path="projects/:id?" element={<ProjectsRoute />} />
        <Route path="print" element={<PrintStationPage />} />
        <Route path="print/freeform" element={<FreeformPrintPage />} />
        {/* Its own address rather than a tab's state: the hardware Back button
            on Android walks the history, and a designer that could not be
            backed out of would trap it. */}
        <Route path="print/templates" element={<TemplatesPage />} />
        <Route path="print/printers" element={<PrinterProfilesPage />} />
        <Route path="p/:shortCode" element={<PrinterProfileQrPage />} />
        <Route path="imports" element={<ImportsPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="audit" element={<AuditRoute />} />
        <Route path="backups" element={<BackupsPage />} />
        <Route path="release" element={<ReleasePage />} />
        <Route path="security" element={<SecurityPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="scan" element={<ScanRoute />} />
        {/* An address nobody recognises is not an error worth a screen of its
            own — the catalogue is where everything starts. */}
        <Route path="*" element={<Navigate to="/components" replace />} />
      </Route>
    </Routes>
  );
}
